import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BudgetLimit, BudgetLimitInput, BudgetsResponse, ExpenseCategory, Trip } from '../../../types';
import { expensesApi } from '../../../api/expenses.api';
import { toast } from '../../ui/Toast';
import { BUDGET_CATEGORIES, tripPeople } from './budgetHelpers';

interface Props {
  trip: Trip;
  limits: BudgetLimit[];
  onSaved: (res: BudgetsResponse) => void;
  onCancel: () => void;
}

// One controlled text field per possible limit; empty string = no limit.
interface Draft {
  total: string;
  daily: string;
  category: Record<string, string>;
  person: Record<string, string>;
}

function initialDraft(limits: BudgetLimit[]): Draft {
  const draft: Draft = { total: '', daily: '', category: {}, person: {} };
  for (const l of limits) {
    const v = String(l.amount);
    if (l.scope === 'TOTAL') draft.total = v;
    else if (l.scope === 'DAILY') draft.daily = v;
    else if (l.scope === 'CATEGORY' && l.category) draft.category[l.category] = v;
    else if (l.scope === 'PERSON' && l.userId) draft.person[l.userId] = v;
  }
  return draft;
}

function toLimits(draft: Draft): BudgetLimitInput[] {
  const out: BudgetLimitInput[] = [];
  const num = (s: string) => {
    const n = Number(s);
    return s.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
  };
  const total = num(draft.total);
  if (total) out.push({ scope: 'TOTAL', amount: total });
  const daily = num(draft.daily);
  if (daily) out.push({ scope: 'DAILY', amount: daily });
  for (const [category, s] of Object.entries(draft.category)) {
    const n = num(s);
    if (n) out.push({ scope: 'CATEGORY', category: category as ExpenseCategory, amount: n });
  }
  for (const [userId, s] of Object.entries(draft.person)) {
    const n = num(s);
    if (n) out.push({ scope: 'PERSON', userId, amount: n });
  }
  return out;
}

// Module-level so it keeps its identity across renders — a component defined
// inside BudgetManager would remount each keystroke and drop input focus.
function AmountField({ value, currency, placeholder, onChange }: {
  value: string; currency: string; placeholder: string; onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <input
        type="number" min="0" step="1" inputMode="decimal"
        className="input w-28 py-1.5 font-mono text-right"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <span className="font-mono text-[10px] text-slate-400 w-7">{currency}</span>
    </div>
  );
}

export function BudgetManager({ trip, limits, onSaved, onCancel }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => initialDraft(limits));
  const [saving, setSaving] = useState(false);
  const currency = trip.budgetCurrency || 'EUR';
  const people = tripPeople(trip);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await expensesApi.setBudgets(trip.id, toLimits(draft));
      toast(t('expenses.budget.saved'), 'success');
      onSaved(res);
    } catch {
      toast(t('expenses.budget.failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  const noLimit = t('expenses.budget.noLimit');

  return (
    <div className="space-y-5">
      {/* ── Whole-trip limits (total / category / person) ───────────────────── */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400">
          {t('expenses.budget.tripSection')}
        </p>
        <p className="text-[11px] text-slate-400 mb-3">{t('expenses.budget.tripSectionHint')}</p>

        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('expenses.budget.total')}</span>
            <AmountField value={draft.total} currency={currency} placeholder={noLimit} onChange={v => setDraft(d => ({ ...d, total: v }))} />
          </div>

          {/* By category */}
          <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-slate-400 pt-2">
            {t('expenses.budget.categorySection')}
          </p>
          {BUDGET_CATEGORIES.map(cat => (
            <div key={cat} className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-600 dark:text-slate-400 truncate">
                {t(`expenses.categories.${cat}` as Parameters<typeof t>[0])}
              </span>
              <AmountField
                value={draft.category[cat] ?? ''}
                currency={currency} placeholder={noLimit}
                onChange={v => setDraft(d => ({ ...d, category: { ...d.category, [cat]: v } }))}
              />
            </div>
          ))}

          {/* By person */}
          {people.length > 1 && (
            <>
              <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-slate-400 pt-2">
                {t('expenses.budget.personSection')}
              </p>
              {people.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-600 dark:text-slate-400 truncate">
                    {p.displayName || p.username}
                  </span>
                  <AmountField
                    value={draft.person[p.id] ?? ''}
                    currency={currency} placeholder={noLimit}
                    onChange={v => setDraft(d => ({ ...d, person: { ...d.person, [p.id]: v } }))}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── Per-day allowance (distinct from the whole-trip limits above) ────── */}
      <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
        <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 pt-3">
          {t('expenses.budget.dailySection')}
        </p>
        <p className="text-[11px] text-slate-400 mb-3">{t('expenses.budget.dailyHint')}</p>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('expenses.budget.daily')}</span>
          <AmountField value={draft.daily} currency={currency} placeholder={noLimit} onChange={v => setDraft(d => ({ ...d, daily: v }))} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
          {t('expenses.budget.save')}
        </button>
      </div>
    </div>
  );
}
